<template>
  <v-container fluid>
    <v-card dark outlined>
      <v-card-title class="secondary py-1 text-subtitle-1">
        <v-icon left dense>mdi-account</v-icon>
        Dashboard Users
        <v-spacer />
        <v-btn x-small dark class="mr-2" @click="openCreate">
          <v-icon left x-small>mdi-plus</v-icon>
          Create New User
        </v-btn>
      </v-card-title>
      <v-card-text>
        <v-text-field
          v-model="search"
          label="Search username"
          dense
          outlined
          clearable
          hide-details
          class="mb-3"
          style="max-width: 320px"
        />
        <v-data-table
          :headers="headers"
          :items="filteredUsers"
          :loading="loading"
          class="elevation-5"
        >
          <template v-slot:item.actions="{ item }">
            <v-icon small class="mr-2" @click="openEdit(item)">mdi-pencil</v-icon>
            <v-icon small @click="confirmDelete(item)">mdi-delete</v-icon>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <v-dialog v-model="createDialog" max-width="480" persistent>
      <v-card dark>
        <v-card-title>Create New User</v-card-title>
        <v-card-text>
          <v-text-field v-model="createForm.UserId" label="Username" outlined dense class="mt-2" />
          <v-text-field v-model="createForm.Email" label="Email" outlined dense />
          <p class="caption grey--text">
            Cognito will email a temporary password to this address.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="createDialog = false">Cancel</v-btn>
          <v-btn color="secondary" :loading="saving" @click="createUser">Create</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="editDialog" max-width="900" persistent scrollable>
      <v-card dark>
        <v-card-title>Edit User — {{ editUser.UserId }}</v-card-title>
        <v-card-text>
          <v-subheader class="px-0">Recent Event</v-subheader>
          <v-row>
            <v-col cols="4">
              <v-select
                v-model="permissionsYear"
                :items="yearOptions"
                label="Year"
                dense
                outlined
                @change="onPermissionsYearChange"
              />
            </v-col>
            <v-col cols="8">
              <v-autocomplete
                v-model="editUser.RecentlyViewed.EventId"
                :items="recentEventOptions"
                item-text="label"
                item-value="EventId"
                label="Recent Event"
                placeholder="Type to search…"
                dense
                outlined
                clearable
                hide-no-data
                :filter="filterRecentEvent"
              />
            </v-col>
          </v-row>

          <v-subheader class="px-0">Direct Permissions</v-subheader>
          <div class="mb-2">
            <v-btn small color="secondary" class="mr-2" @click="addPermDialog = true">
              <v-icon left small>mdi-plus</v-icon>
              Add Permissions
            </v-btn>
            <v-btn small outlined @click="managePermDialog = true">
              <v-icon left small>mdi-eye</v-icon>
              View / Manage Permissions
            </v-btn>
            <span class="caption grey--text ml-3">
              {{ (editUser.Events || []).length }} event permission(s)
            </span>
          </div>

          <v-subheader class="px-0 mt-4">Permissions from Groups (read-only)</v-subheader>
          <div v-if="!(editUser.GroupPermissions || []).length" class="caption grey--text">
            Not a member of any groups.
          </div>
          <v-card
            v-for="g in editUser.GroupPermissions || []"
            :key="g.UserGroupId"
            outlined
            class="mb-3"
          >
            <v-card-title class="subtitle-2">
              {{ g.UserGroupName }}
              <v-spacer />
              <v-btn x-small text @click="openGroupView(g)">
                View
              </v-btn>
            </v-card-title>
            <v-card-text class="caption grey--text py-1">
              {{ (g.Events || []).length }} event permission(s) — uses Year filter above
            </v-card-text>
          </v-card>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="editDialog = false">Cancel</v-btn>
          <v-btn color="secondary" :loading="saving" @click="saveUser">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <add-permissions-dialog
      v-model="addPermDialog"
      :events="editUser.Events"
      :default-year="permissionsYear"
      @update:events="onDirectEventsUpdated"
      @added="onPermissionsAdded"
    />

    <manage-permissions-dialog
      v-model="managePermDialog"
      :events="editUser.Events"
      :can-edit="true"
      :year-filter="permissionsYear"
      @update:events="onDirectEventsUpdated"
    />

    <manage-permissions-dialog
      v-model="groupViewDialog"
      :events="groupViewEvents"
      :can-edit="false"
      :year-filter="permissionsYear"
    />

    <delete-confirmation-dialog
      v-model="deleteDialog"
      title="Delete this dashboard user (Cognito + Dynamo)?"
      @confirm="deleteUser"
      @cancel="deleteDialog = false"
    />
  </v-container>
</template>

<script>
import { mapState } from "vuex";
import { dashboardAPI } from "@/services";
import AddPermissionsDialog from "@/components/admin/AddPermissionsDialog.vue";
import ManagePermissionsDialog from "@/components/admin/ManagePermissionsDialog.vue";
import DeleteConfirmationDialog from "@/components/dialogs/DeleteConfirmationDialog.vue";
import iziToast from "izitoast/dist/js/iziToast.min.js";
import "izitoast/dist/css/iziToast.min.css";

export default {
  name: "DashboardUsers",
  components: {
    AddPermissionsDialog,
    ManagePermissionsDialog,
    DeleteConfirmationDialog,
  },
  data() {
    const y = new Date().getFullYear();
    return {
      loading: false,
      saving: false,
      users: [],
      search: "",
      headers: [
        { text: "Username", value: "UserId" },
        { text: "Email", value: "Email" },
        { text: "Recent Event", value: "RecentEventName" },
        { text: "Actions", value: "actions", sortable: false },
      ],
      createDialog: false,
      createForm: { UserId: "", Email: "" },
      editDialog: false,
      editUser: { UserId: "", Events: [], RecentlyViewed: {}, GroupPermissions: [] },
      deleteDialog: false,
      deleteTarget: null,
      yearOptions: [y + 1, y, y - 1, y - 2, y - 3, y - 4, y - 5],
      permissionsYear: y,
      recentEventOptions: [],
      addPermDialog: false,
      managePermDialog: false,
      groupViewDialog: false,
      groupViewEvents: [],
    };
  },
  computed: {
    ...mapState("filter", ["IsAdmin"]),
    filteredUsers() {
      const q = (this.search || "").toLowerCase();
      if (!q) return this.users;
      return this.users.filter((u) => (u.UserId || "").toLowerCase().includes(q));
    },
  },
  created() {
    if (!this.IsAdmin) {
      this.$router.replace("/");
      return;
    }
    this.loadUsers();
  },
  methods: {
    async loadUsers() {
      this.loading = true;
      try {
        const data = await dashboardAPI.getDashboardAccessAPI().getDashboardUsers();
        this.users = data.Users || [];
      } catch (e) {
        iziToast.error({ title: "Users", message: "Failed to load users", position: "topRight" });
      } finally {
        this.loading = false;
      }
    },
    openCreate() {
      this.createForm = { UserId: "", Email: "" };
      this.createDialog = true;
    },
    async createUser() {
      this.saving = true;
      try {
        const data = await dashboardAPI.getDashboardAccessAPI().postDashboardUsers({
          Action: "Create",
          UserId: this.createForm.UserId,
          Email: this.createForm.Email,
        });
        this.createDialog = false;
        iziToast.success({ title: "Users", message: "User created", position: "topRight" });
        await this.loadUsers();
        if (data.User) this.openEdit(data.User);
      } catch (e) {
        iziToast.error({
          title: "Users",
          message: e?.response?.data?.Error || "Create failed",
          position: "topRight",
        });
      } finally {
        this.saving = false;
      }
    },
    openEdit(item) {
      const y = new Date().getFullYear();
      this.editUser = {
        UserId: item.UserId,
        Events: JSON.parse(JSON.stringify(item.Events || [])),
        RecentlyViewed: {
          EventId: item.RecentlyViewed?.EventId || item.RecentEventId || "",
        },
        GroupPermissions: JSON.parse(JSON.stringify(item.GroupPermissions || [])),
      };
      // Always default filter to current year (number) so the Year select shows it
      this.permissionsYear = y;
      this.addPermDialog = false;
      this.managePermDialog = false;
      this.groupViewDialog = false;
      this.editDialog = true;
      this.loadRecentEvents();
    },
    onPermissionsYearChange() {
      this.loadRecentEvents();
    },
    onDirectEventsUpdated(next) {
      this.editUser.Events = next;
    },
    onPermissionsAdded() {
      this.loadRecentEvents();
    },
    openGroupView(g) {
      this.groupViewEvents = g.Events || [];
      this.groupViewDialog = true;
    },
    filterRecentEvent(item, queryText) {
      const q = (queryText || "").toLowerCase();
      if (!q) return true;
      const label = (item.label || "").toLowerCase();
      const id = (item.EventId || "").toLowerCase();
      return label.includes(q) || id.includes(q);
    },
    async loadRecentEvents() {
      const ids = new Set(
        (this.editUser.Events || [])
          .map((e) => e.EventId)
          .concat(
            (this.editUser.GroupPermissions || []).flatMap((g) =>
              (g.Events || []).map((e) => e.EventId)
            )
          )
      );
      try {
        const api = dashboardAPI.getAdminGroupsEventsAPI();
        const data = await api.getEventsData(this.permissionsYear);
        const list = Array.isArray(data) ? data : data?.Events || [];
        this.recentEventOptions = list
          .filter((e) => ids.has(e.EventId) || ids.size === 0)
          .map((e) => ({
            EventId: e.EventId,
            label: `${e.EventName} (${e.EventId})`,
          }));
      } catch (e) {
        this.recentEventOptions = [];
      }
    },
    async saveUser() {
      this.saving = true;
      try {
        await dashboardAPI.getDashboardAccessAPI().postDashboardUsers({
          Action: "Update",
          UserId: this.editUser.UserId,
          Events: this.editUser.Events,
          RecentlyViewed: this.editUser.RecentlyViewed,
        });
        this.editDialog = false;
        iziToast.success({ title: "Users", message: "Saved", position: "topRight" });
        this.loadUsers();
      } catch (e) {
        iziToast.error({
          title: "Users",
          message: e?.response?.data?.Error || "Save failed",
          position: "topRight",
        });
      } finally {
        this.saving = false;
      }
    },
    confirmDelete(item) {
      this.deleteTarget = item;
      this.deleteDialog = true;
    },
    async deleteUser() {
      try {
        await dashboardAPI.getDashboardAccessAPI().postDashboardUsers({
          Action: "Delete",
          UserId: this.deleteTarget.UserId,
        });
        iziToast.success({ title: "Users", message: "Deleted", position: "topRight" });
        this.deleteDialog = false;
        this.loadUsers();
      } catch (e) {
        iziToast.error({
          title: "Users",
          message: e?.response?.data?.Error || "Delete failed",
          position: "topRight",
        });
      }
    },
  },
};
</script>
